import { useContext, useEffect, useState } from "react"
import UserContext from "../context/userContext"
import { Link } from "react-router-dom"
import { useSnackbar } from "../context/snackbar"
const Cart = () => {
  const [cart, setCart] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { user } = useContext(UserContext)
  const { showSnackbar } = useSnackbar()
  /* =========================
     FETCH CART
  ========================= */
  const fetchCart = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/me/cart`, {
        credentials: "include",
      })

      if (!res.ok) throw new Error("Failed to fetch cart")

      const data = await res.json()
      setCart(data)
    } catch (err) {
      console.error("FETCH CART ERROR:", err)
      setCart([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      fetchCart()
    } else {
      setCart([])
      setIsLoading(false)
    }
  }, [user])

  /* =========================
     HELPERS
  ========================= */
  const calcTotal = () => {
    return cart
      .reduce((total, item) => total + item.product_price * item.qty, 0)
      .toFixed(2)
  }

  const countItems = () => {
    return cart.reduce((count, item) => count + Number(item.qty), 0)
  }

  /* =========================
     UPDATE QTY
  ========================= */
  const updateItemQty = async (product_id, qty) => {
    try {
      const newCart = cart.map((item) =>
        item.product_id === product_id ? { ...item, qty } : item,
      )

      setCart(newCart)

      await fetch(`${process.env.REACT_APP_API_URL}/me/cart`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ cart: newCart }),
      })
    } catch (err) {
      console.error("UPDATE QTY ERROR:", err)
    }
  }

  /* =========================
     REMOVE ITEM
  ========================= */
  const removeFromCart = async (product_id) => {
    try {
      const newCart = cart.filter((item) => item.product_id !== product_id)

      setCart(newCart)

      await fetch(`${process.env.REACT_APP_API_URL}/me/cart/${product_id}`, {
        method: "DELETE",
        credentials: "include",
      })

      showSnackbar("Item removed from cart 🗑️", "success")
    } catch (err) {
      console.error("REMOVE ITEM ERROR:", err)
      showSnackbar("Failed to remove item", "error")
    }
  }

  /* =========================
     LOADING
  ========================= */
  if (isLoading) return <div className="p-5 text-center">Loading...</div>

  /* =========================
     NOT LOGGED IN
  ========================= */
  if (!user) {
    return (
      <div className="Cart d-flex flex-column justify-content-center col-md-6 col-10 mx-auto my-auto">
        <h1 className="display-5 mb-3">Your cart is empty.</h1>
        <h2 className="lead fs-5">
          Please log in to start adding items to your cart
        </h2>

        <div className="d-flex gap-3 justify-content-center mt-4">
          <Link className="btn btn-primary" to="/login">
            Log in
          </Link>
          <Link className="btn btn-outline-primary" to="/signup">
            Sign up
          </Link>
        </div>
      </div>
    )
  }

  /* =========================
     EMPTY CART
  ========================= */
  if (cart.length === 0) {
    return (
      <div className="Cart my-5 text-center">
        <h2>Your cart is empty</h2>
        <Link className="btn btn-primary mt-3" to="/products">
          Browse products
        </Link>
      </div>
    )
  }

  /* =========================
     UI
  ========================= */
  const peso = (n) =>
    new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(n)

  return (
    <div className="Cart container my-4 my-md-5 px-3 text-start" style={{ maxWidth: 820 }}>
      <h1 className="h2 mb-3">Shopping Cart</h1>

      <ul className="list-unstyled mb-0">
        {cart.map((item) => {
          const qtyOptions = Array.from({ length: Math.max(20, item.qty) }, (_, k) => k + 1)
          return (
            <li key={item.product_id} className="d-flex gap-3 border-bottom py-3">
              {/* IMAGE */}
              <Link to={`/products/${item.product_id}`} className="flex-shrink-0" tabIndex={-1} aria-hidden="true">
                <img
                  src={item.image}
                  alt=""
                  width={72}
                  height={72}
                  className="rounded-2"
                  style={{ objectFit: "cover", background: "#f8f9fa" }}
                />
              </Link>

              {/* DETAILS */}
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex justify-content-between gap-2">
                  <Link
                    to={`/products/${item.product_id}`}
                    className="text-body fw-semibold text-decoration-none"
                  >
                    {item.product_name}
                  </Link>
                  <div className="fw-bold text-nowrap">{peso(item.product_price * item.qty)}</div>
                </div>
                <div className="small text-muted">{peso(item.product_price)} / case</div>

                <div className="d-flex align-items-center gap-2 mt-2">
                  <label htmlFor={`qty-${item.product_id}`} className="small text-muted">
                    Cases
                  </label>
                  <select
                    id={`qty-${item.product_id}`}
                    className="form-select form-select-sm"
                    style={{ width: "5rem" }}
                    value={item.qty}
                    onChange={(e) => updateItemQty(item.product_id, Number(e.target.value))}
                  >
                    {qtyOptions.map((q) => (
                      <option key={q} value={q}>
                        {q}
                      </option>
                    ))}
                  </select>

                  <button
                    className="btn btn-link btn-sm text-danger ms-auto px-0"
                    onClick={() => removeFromCart(item.product_id)}
                    aria-label={`Remove ${item.product_name} from cart`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      {/* SUMMARY */}
      <div className="d-flex flex-column align-items-stretch align-items-sm-end mt-4">
        <div className="fs-5 mb-3">
          Subtotal ({countItems()} {countItems() === 1 ? "case" : "cases"}):{" "}
          <strong>{peso(Number(calcTotal()))}</strong>
        </div>

        <Link to="/checkout" className="btn btn-success btn-lg">
          Checkout
        </Link>
      </div>
    </div>
  )
}

export default Cart
